import { useEffect, useState } from "react";
import { UserPlus, Globe, Handshake, PenLine, Search, UserCheck, Plus } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import Modal from "../components/Modal";
import TreatmentSelect from "../components/TreatmentSelect";
import { inputCls, labelCls, toApiDate } from "../utils/datetime";

const LEAD_STYLES = {
  New: "bg-gold-soft/70 text-pine-deep",
  Contacted: "bg-blue-50 text-blue-600",
  "Follow-up": "bg-purple-50 text-purple-600",
  Converted: "bg-teal-soft text-teal-brand",
  Lost: "bg-red-50 text-red-500",
};
const STATUSES = Object.keys(LEAD_STYLES);
const EMPTY = { name: "", phone: "", source: "Walk-in", treatment: "", assigned_staff: "", followup_date: "", notes: "" };

export default function Leads() {
  const [leads, setLeads] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState("All");
  const [query, setQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);

  const load = () => {
    api.get("/connect/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/connect/users").then((r) => setStaff(r.data || [])).catch(() => {});
  };
  useEffect(() => { load(); }, []);

  const setStatus = async (id, lead_status) => {
    try { await api.patch(`/connect/leads/${id}`, { lead_status }); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Could not update status"); }
  };

  const add = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/connect/leads", { ...form, followup_date: toApiDate(form.followup_date) });
      toast.success(`Lead saved — ${r.data.lead_id}`);
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not save lead"); }
  };

  const openEdit = (l) => {
    if (l.lead_type !== "Manual") return;
    setEditing(l);
    setEditForm({
      name: l.name || "", phone: l.phone || "", source: l.source || "Walk-in",
      treatment: l.treatment || "", assigned_staff: l.assigned_staff || "",
      followup_date: l.followup_date || "", notes: l.notes || "",
    });
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      await api.patch(`/connect/leads/${editing.id}`, editForm);
      toast.success("Lead updated");
      setEditing(null); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not update lead"); }
  };

  const convert = async (l) => {
    try {
      const r = await api.post(`/connect/leads/${l.id}/convert`);
      toast.success(`${l.name} converted — patient ${r.data.patient.patient_id} created`);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Could not convert lead"); }
  };

  const filtered = leads.filter((l) => {
    if (filter !== "All" && l.lead_type !== filter) return false;
    const q = query.trim().toLowerCase();
    return !q || [l.name, l.lead_id, l.phone, l.source, l.treatment].some((v) => String(v || "").toLowerCase().includes(q));
  });

  return (
    <div data-testid="connect-leads" className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="font-display text-2xl font-semibold text-pine-deep">Leads</h2>
          <p className="text-sm text-slate-500">Website enquiries, franchise interest and walk-in leads — track to conversion.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          {["All", "Appointment", "Franchise", "Manual"].map((f) => (
            <button key={f} data-testid={`leads-filter-${f.toLowerCase()}`} onClick={() => setFilter(f)}
              className={`rounded-full px-4 py-2 text-xs font-bold border transition-all ${filter === f ? "bg-pine text-ivory border-pine" : "bg-white text-pine border-pine/15"}`}>
              {f}
            </button>
          ))}
          <button data-testid="leads-add-toggle" onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 rounded-full bg-teal-brand px-4 py-2 text-xs font-bold text-white hover:bg-pine transition-all">
            <Plus className="h-4 w-4" /> Add Lead
          </button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <input data-testid="leads-search" value={query} onChange={(e) => setQuery(e.target.value)}
          placeholder="Search leads — name, Lead ID, phone, source…"
          className="h-11 w-full rounded-xl border border-pine/15 bg-white pl-11 pr-4 text-sm focus:outline-none focus:ring-2 focus:ring-pine" />
      </div>

      {showForm && (
        <form data-testid="lead-add-form" onSubmit={add} className="rounded-2xl bg-white border border-pine/8 shadow-soft p-5 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div><label className={labelCls}>Name *</label>
            <input required data-testid="lead-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
          <div><label className={labelCls}>Phone *</label>
            <input required type="tel" inputMode="tel" data-testid="lead-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
          <div><label className={labelCls}>Source</label>
            <select data-testid="lead-source" value={form.source} onChange={(e) => setForm({ ...form, source: e.target.value })} className={`mt-1 ${inputCls}`}>
              {["Walk-in", "Phone Call", "Google", "Instagram", "Referral", "Website", "Other"].map((s) => <option key={s}>{s}</option>)}
            </select></div>
          <div><label className={labelCls}>Interested Treatment</label>
            <div className="mt-1"><TreatmentSelect testid="lead-treatment" value={form.treatment} onChange={(v) => setForm({ ...form, treatment: v })} /></div></div>
          <div><label className={labelCls}>Assigned Staff</label>
            <select data-testid="lead-assigned" value={form.assigned_staff} onChange={(e) => setForm({ ...form, assigned_staff: e.target.value })} className={`mt-1 ${inputCls}`}>
              <option value="">Unassigned</option>
              {staff.map((s) => <option key={s.email} value={s.name}>{s.name} ({s.role})</option>)}
            </select></div>
          <div><label className={labelCls}>Follow-up Date</label>
            <input type="date" data-testid="lead-followup-date" value={form.followup_date} onChange={(e) => setForm({ ...form, followup_date: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
          <div className="sm:col-span-2 lg:col-span-3"><label className={labelCls}>Notes</label>
            <textarea data-testid="lead-notes" rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" /></div>
          <div><button type="submit" data-testid="lead-submit" className="h-11 w-full rounded-full bg-pine text-ivory text-xs font-bold hover:bg-pine-deep transition-colors">Save Lead</button></div>
        </form>
      )}

      <div className="rounded-2xl bg-white border border-pine/8 shadow-soft overflow-hidden">
        <div className="overflow-x-auto" data-lenis-prevent>
          <table className="w-full text-sm min-w-[860px]">
            <thead>
              <tr className="border-b border-pine/8 text-left text-[11px] uppercase tracking-wider text-slate-400">
                <th className="px-5 py-3.5 font-bold">Lead</th>
                <th className="px-5 py-3.5 font-bold">Type</th>
                <th className="px-5 py-3.5 font-bold">Details</th>
                <th className="px-5 py-3.5 font-bold">Contact</th>
                <th className="px-5 py-3.5 font-bold">Assigned</th>
                <th className="px-5 py-3.5 font-bold">Status</th>
                <th className="px-5 py-3.5 font-bold">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((l) => (
                <tr key={l.id} data-testid={`lead-row-${l.id.slice(0, 8)}`} className="border-b border-pine/5 last:border-0 hover:bg-cream/50 transition-colors">
                  <td className="px-5 py-3.5 font-bold text-pine-deep">
                    {l.name}
                    {l.lead_id && <span className="ml-1.5 rounded-full bg-cream px-2 py-0.5 text-[10px] font-bold text-pine">{l.lead_id}</span>}
                    <p className="text-[10px] font-normal text-slate-400">{l.created_at ? new Date(l.created_at).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "-"}</p>
                  </td>
                  <td className="px-5 py-3.5">
                    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-bold ${l.lead_type === "Franchise" ? "bg-gold-soft/60 text-pine-deep" : l.lead_type === "Manual" ? "bg-purple-50 text-purple-600" : "bg-teal-soft text-teal-brand"}`}>
                      {l.lead_type === "Franchise" ? <Handshake className="h-3 w-3" /> : l.lead_type === "Manual" ? <PenLine className="h-3 w-3" /> : <Globe className="h-3 w-3" />}
                      {l.lead_type}
                    </span>
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">
                    {l.lead_type === "Franchise" ? `${l.city} · ${l.investment}` : `${l.service || l.treatment || "—"}${l.followup_date ? ` · FU: ${l.followup_date}` : ""}`}
                    {l.notes && <p className="text-[10px] text-slate-400 italic line-clamp-1">"{l.notes}"</p>}
                  </td>
                  <td className="px-5 py-3.5 text-slate-600">
                    <p>{l.phone}</p><p className="text-xs text-slate-400">{l.email || ""}</p>
                  </td>
                  <td className="px-5 py-3.5 text-xs text-slate-500">{l.assigned_staff || "—"}</td>
                  <td className="px-5 py-3.5">
                    <select
                      data-testid={`lead-status-${l.id.slice(0, 8)}`}
                      value={l.lead_status || "New"}
                      onChange={(e) => setStatus(l.id, e.target.value)}
                      className={`rounded-full px-3 py-1.5 text-[11px] font-bold border-0 cursor-pointer ${LEAD_STYLES[l.lead_status || "New"]}`}
                    >
                      {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </td>
                  <td className="px-5 py-3.5">
                    <div className="flex gap-2">
                      {l.lead_type === "Manual" && (
                        <button data-testid={`lead-edit-${l.id.slice(0, 8)}`} onClick={() => openEdit(l)}
                          className="rounded-full border border-pine/15 px-3 py-1.5 text-[10px] font-bold text-pine hover:bg-cream">Edit</button>
                      )}
                      {l.lead_type !== "Franchise" && l.lead_status !== "Converted" && (
                        <button data-testid={`lead-convert-${l.id.slice(0, 8)}`} onClick={() => convert(l)}
                          className="inline-flex items-center gap-1 rounded-full bg-teal-brand px-3 py-1.5 text-[10px] font-bold text-white hover:bg-pine">
                          <UserCheck className="h-3 w-3" /> Convert
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="px-5 py-10 text-center text-slate-400">
                  <UserPlus className="h-8 w-8 mx-auto mb-2 text-slate-300" /> No leads yet — share your website or add one manually.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal testid="lead-edit-modal" onClose={() => setEditing(null)} title={editing.name} subtitle={`${editing.lead_id} · ${editing.source}`}>
          <form onSubmit={saveEdit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Name</label>
                <input required data-testid="lead-edit-name" value={editForm.name} onChange={(e) => setEditForm({ ...editForm, name: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
              <div><label className={labelCls}>Phone</label>
                <input required type="tel" inputMode="tel" data-testid="lead-edit-phone" value={editForm.phone} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className={labelCls}>Source</label>
                <select data-testid="lead-edit-source" value={editForm.source} onChange={(e) => setEditForm({ ...editForm, source: e.target.value })} className={`mt-1 ${inputCls}`}>
                  {["Walk-in", "Phone Call", "Google", "Instagram", "Referral", "Website", "Other"].map((s) => <option key={s}>{s}</option>)}
                </select></div>
              <div><label className={labelCls}>Assigned Staff</label>
                <select data-testid="lead-edit-assigned" value={editForm.assigned_staff} onChange={(e) => setEditForm({ ...editForm, assigned_staff: e.target.value })} className={`mt-1 ${inputCls}`}>
                  <option value="">Unassigned</option>
                  {staff.map((s) => <option key={s.email} value={s.name}>{s.name}</option>)}
                </select></div>
            </div>
            <div>
              <label className={labelCls}>Interested Treatment</label>
              <div className="mt-1"><TreatmentSelect testid="lead-edit-treatment" value={editForm.treatment} onChange={(v) => setEditForm({ ...editForm, treatment: v })} /></div>
            </div>
            <div><label className={labelCls}>Follow-up Date</label>
              <input type="date" data-testid="lead-edit-followup" value={editForm.followup_date} onChange={(e) => setEditForm({ ...editForm, followup_date: e.target.value })} className={`mt-1 ${inputCls}`} /></div>
            <div><label className={labelCls}>Notes</label>
              <textarea data-testid="lead-edit-notes" rows={2} value={editForm.notes} onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                className="mt-1 w-full rounded-xl border border-pine/15 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-pine bg-white" /></div>
            <div className="flex gap-3">
              <button type="submit" data-testid="lead-edit-save" className="flex-1 rounded-full bg-pine px-5 py-3 text-sm font-bold text-ivory hover:bg-pine-deep transition-colors">Save Changes</button>
              <button type="button" onClick={() => setEditing(null)} className="rounded-full border border-pine/20 px-5 py-3 text-sm font-bold text-pine hover:bg-cream transition-colors">Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}
